import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const isTransparent = isHome && !scrolled;

  const linkClass = isTransparent
    ? "text-background-50/90 hover:text-background-50"
    : "text-foreground-700 hover:text-foreground-950";

  const dropdownBtnClass = isTransparent
    ? "bg-background-50/15 backdrop-blur-sm text-background-50 hover:bg-background-50/25"
    : "bg-background-100/80 text-foreground-700 hover:bg-background-200";

  const outlineBtnClass = isTransparent
    ? "border-background-50/40 text-background-50 hover:border-background-50/80 hover:text-background-50"
    : "border-foreground-300 text-foreground-800 hover:text-foreground-950 hover:border-foreground-500";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : isHome
            ? "bg-gradient-to-b from-black/30 to-transparent"
            : "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Brand + Home */}
          <div className="flex items-center gap-4 md:gap-6">
            <Link
              to="/"
              className={`font-heading text-xl md:text-2xl transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isTransparent ? "text-background-50" : "text-foreground-950 hover:text-primary-600"
              }`}
            >
              acbuy.yachts
            </Link>
            <Link
              to="/"
              className={`hidden md:inline-flex text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${linkClass}`}
            >
              Home
            </Link>
            <Link
              to="/article/acbuyspreadsheet-shopping-guide"
              className={`hidden md:inline-flex text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${linkClass}`}
            >
              Shopping Guide
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4">
            {/* Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${dropdownBtnClass}`}
              >
                Browse Categories
                <i className={dropdownOpen ? "ri-arrow-up-s-line text-base transition-transform duration-200" : "ri-arrow-down-s-line text-base transition-transform duration-200"}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200/80 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-primary-50 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-8 h-8 flex items-center justify-center bg-background-100 rounded-lg flex-shrink-0">
                        <i className="ri-shopping-bag-line text-foreground-500"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Nav Links */}
            <Link
              to="/what-is-acbuyspreadsheet"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${linkClass}`}
            >
              Guide
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-medium border rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${outlineBtnClass}`}
            >
              acbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isTransparent ? "text-background-50" : "text-foreground-800"
            }`}
          >
            <i className={`ri-${mobileMenuOpen ? "close" : "menu"}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {/* Quick links */}
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg">
                <i className="ri-home-line text-primary-600"></i>
              </span>
              Home
            </Link>
            <Link
              to="/what-is-acbuyspreadsheet"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-lg">
                <i className="ri-book-open-line text-accent-600"></i>
              </span>
              Guide
            </Link>
            <Link
              to="/article/acbuyspreadsheet-shopping-guide"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-lg">
                <i className="ri-shopping-cart-line text-secondary-600"></i>
              </span>
              Shopping Guide
            </Link>

            <div className="my-3 border-t border-background-200/70"></div>

            <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              Product Categories
            </p>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 flex items-center justify-center bg-background-200 rounded-lg flex-shrink-0">
                  <i className="ri-arrow-right-up-line text-foreground-400 text-sm"></i>
                </span>
                {cat.name}
              </a>
            ))}

            <div className="my-3 border-t border-background-200/70"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium border border-foreground-300 text-foreground-800 rounded-xl transition-all cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center bg-background-200 rounded-lg">
                <i className="ri-external-link-line text-foreground-500"></i>
              </span>
              acbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-xl transition-all cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center bg-background-50/20 rounded-lg">
                <i className="ri-shopping-bag-line text-background-50"></i>
              </span>
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}